import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Switch } from 'components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import LoadingState from 'components/shared/LoadingState';
import { cn } from 'lib/utils';
import { getUsers, setAdminStatus, setContentCreatorStatus } from '../../services/api';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  // Debounced copy of `search` — the ONLY search value fetchUsers reads, so a
  // keystroke triggers exactly one request (after the 300ms timer), never two.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [updating, setUpdating] = useState(null);
  const [updatingCreator, setUpdatingCreator] = useState(null);
  const searchInputRef = useRef(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const response = await getUsers({
        page,
        page_size: 25,
        search: debouncedSearch || undefined,
      });

      setUsers(response.users || []);
      setTotal(response.total || 0);
      setTotalPages(response.total_pages || 1);
    } catch (err) {
      setLoadError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounce search input → both setters land in one render, so the fetch
  // effect above fires once per settled keystroke burst.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // Press '/' anywhere to jump to the search box
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== '/' || event.defaultPrevented) return;
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      event.preventDefault();
      searchInputRef.current?.focus();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleAdminToggle = async (user) => {
    setUpdating(user.wp_id);

    try {
      await setAdminStatus({
        wp_id: user.wp_id,
        is_admin: !user.is_admin,
      });

      setUsers((prev) =>
        prev.map((u) =>
          u.wp_id === user.wp_id ? { ...u, is_admin: !u.is_admin } : u
        )
      );
    } catch (err) {
      toast.error(err.message || 'Failed to update admin status');
    } finally {
      setUpdating(null);
    }
  };

  const handleContentCreatorToggle = async (user) => {
    setUpdatingCreator(user.wp_id);

    try {
      await setContentCreatorStatus({
        wp_id: user.wp_id,
        is_content_creator: !user.is_content_creator,
      });

      setUsers((prev) =>
        prev.map((u) =>
          u.wp_id === user.wp_id
            ? { ...u, is_content_creator: !u.is_content_creator }
            : u
        )
      );
    } catch (err) {
      toast.error(err.message || 'Failed to update content creator status');
    } finally {
      setUpdatingCreator(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div>
      <h2 className="text-token-xl font-semibold">User Management</h2>
      <p className="mb-6 text-sm text-muted-foreground">
        Manage administrator access. Users marked as admin can access this admin panel.
      </p>

      {loadError && (
        <Alert variant="destructive" className="mb-4 flex items-center justify-between gap-4">
          <AlertDescription>{loadError}</AlertDescription>
          <Button variant="outline" size="sm" onClick={fetchUsers}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Search + stats */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="relative w-[300px]">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            ref={searchInputRef}
            placeholder="Search by email or name..."
            title="Press / to focus"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 text-sm"
          />
        </div>
        <Badge variant="outline">{total} total users</Badge>
        <Badge variant="info">
          {users.filter((u) => u.is_admin).length} admins on this page
        </Badge>
      </div>

      {/* Table */}
      <div className="overflow-auto rounded-token-large border border-borderGrey bg-white">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>User</TableHead>
              <TableHead>WP ID</TableHead>
              <TableHead>Auth Source</TableHead>
              <TableHead>Subscription</TableHead>
              <TableHead>Last Login</TableHead>
              <TableHead className="text-center">Admin</TableHead>
              <TableHead className="text-center">Content Creator</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7}>
                  <LoadingState message="Loading users..." minHeight={160} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7} className="py-10 text-center text-token-md text-muted-foreground">
                  {debouncedSearch ? 'No users found matching your search' : 'No users found'}
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.wp_id}
                  className={cn(
                    user.is_admin && 'bg-brand-purple1/5 hover:bg-brand-purple1/10'
                  )}
                >
                  <TableCell>
                    <p className="text-token-md font-medium">
                      {user.wp_display_name || user.email}
                    </p>
                    <p className="text-token-base text-muted-foreground">{user.email}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{user.wp_id}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.auth_source === 'standalone' ? 'success' : 'outline'}>
                      {user.auth_source || 'wordpress'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.has_active_subscription ? 'success' : 'outline'}>
                      {user.has_active_subscription ? 'Active' : 'None'}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-token-md">
                    {formatDate(user.last_login_at)}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center gap-1.5 align-middle">
                      <Switch
                        checked={!!user.is_admin}
                        onCheckedChange={() => handleAdminToggle(user)}
                        disabled={updating === user.wp_id}
                      />
                      {/* Fixed-size spinner slot — no layout shift while saving */}
                      <span className="inline-flex h-4 w-4 items-center justify-center">
                        {updating === user.wp_id && (
                          <Loader2
                            className="h-4 w-4 animate-spin text-muted-foreground"
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center gap-1.5 align-middle">
                      <Switch
                        checked={!!user.is_content_creator}
                        onCheckedChange={() => handleContentCreatorToggle(user)}
                        disabled={updatingCreator === user.wp_id}
                        className="data-[state=checked]:bg-secondary"
                      />
                      <span className="inline-flex h-4 w-4 items-center justify-center">
                        {updatingCreator === user.wp_id && (
                          <Loader2
                            className="h-4 w-4 animate-spin text-muted-foreground"
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            <ChevronLeft aria-hidden="true" />
            Prev
          </Button>
          <span className="text-token-md text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Next
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>
      )}
    </div>
  );
}
